import { authClient } from "@/lib/authClient";
import { zodResolver } from "@hookform/resolvers/zod";
import { generatePassword } from "@repo/lib/stringUtils";
import { toError } from "@repo/lib/utils";
import { Button } from "@repo/ui/components/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@repo/ui/components/Dialog";
import { FieldGroup } from "@repo/ui/components/Field";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@repo/ui/components/Form";
import { Input } from "@repo/ui/components/Input";
import { LoadingWrapper } from "@repo/ui/components/LoadingWrapper";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/Select";
import { useConfirmDialog } from "@repo/ui/hooks/useConfirmDialog";
import { toast } from "@repo/ui/lib/toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader, Plus } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

const inviteUserSchema = z.object({
  email: z.email("Valid email is required"),
  name: z.string().trim().min(1, "Name is required"),
  role: z.enum(["user", "admin"]),
});

type InviteUserFormData = z.infer<typeof inviteUserSchema>;

const defaultValues: InviteUserFormData = {
  email: "",
  name: "",
  role: "user",
};

export const InviteUserDialog = () => {
  const queryClient = useQueryClient();
  const confirm = useConfirmDialog();

  const [open, setOpen] = useState(false);

  const form = useForm<InviteUserFormData>({
    resolver: zodResolver(inviteUserSchema),
    defaultValues,
  });

  const { mutate: createUser, isPending } = useMutation({
    mutationFn: async ({ email, name, role }: InviteUserFormData) => {
      const tempPassword = generatePassword();

      const response = await authClient.admin.createUser({
        email,
        name,
        password: tempPassword,
        role,
        data: {
          requiresPasswordChange: true,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to create user");
      }

      return { tempPassword };
    },
    onSuccess: ({ tempPassword }) => {
      form.reset();
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      setOpen(false);

      confirm({
        title: "User Created Successfully",
        message: (
          <div className="space-y-3">
            <p>A temporary password has been generated for this user.</p>
            <div className="bg-muted rounded-md p-3">
              <p className="mb-1 text-sm font-medium">Temporary Password:</p>
              <code className="text-primary font-mono text-lg font-semibold break-all">
                {tempPassword}
              </code>
            </div>
            <p className="text-muted-foreground text-sm">
              Click the button below to copy the password to your clipboard. The user will be
              required to change it on their first login.
            </p>
          </div>
        ),
        confirmLabel: "Copy Password",
        alert: true,
        onConfirm: async () => {
          await navigator.clipboard.writeText(tempPassword);
          toast.success("Password copied to clipboard!");
        },
      });
    },
    onError: (err) => {
      toast.error(toError(err).message);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button color="primary">
          <Plus className="mr-2 h-4 w-4" />
          Invite User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            Create a new user account. A temporary password will be generated that you can share
            with the user.
          </DialogDescription>
        </DialogHeader>
        <LoadingWrapper isLoading={isPending}>
          <Form {...form}>
            <form
              noValidate
              onSubmit={(e) => void form.handleSubmit((data) => createUser(data))(e)}
            >
              <FieldGroup>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input placeholder="email@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name *</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role *</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FieldGroup>

              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" color="primary" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting && <Loader className="mr-2 h-4 w-4 animate-spin" />}
                  Create User
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </LoadingWrapper>
      </DialogContent>
    </Dialog>
  );
};
